import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, CheckCircle2, Mail, MailX } from 'lucide-react';
import { useMemo, useState } from 'react';
import Card from '../../components/ui/Card';
import KpiTile from '../../components/ui/KpiTile';
import PageHeader from '../../components/ui/PageHeader';
import { alarmsApi, type AlarmDto } from '../../services/alarmsApi';
import { machinesApi } from '../../services/machinesApi';

const SEVERITY_STYLE: Record<AlarmDto['severity'], { dot: string; pill: string; label: string }> = {
  critical: {
    dot: 'bg-red-500',
    pill: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    label: 'Critical',
  },
  fault: {
    dot: 'bg-amber-500',
    pill: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    label: 'Fault',
  },
  warning: {
    dot: 'bg-slate-400',
    pill: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
    label: 'Warning',
  },
};

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const AlarmsView = () => {
  const [machineFilter, setMachineFilter] = useState<string>('');
  const [severityFilter, setSeverityFilter] = useState<string>('');

  const machinesQuery = useQuery({
    queryKey: ['machines'],
    queryFn: async () => (await machinesApi.list()).data,
  });

  const alarmsQuery = useQuery({
    queryKey: ['alarms', machineFilter || 'all'],
    queryFn: async () => (await alarmsApi.list(machineFilter || undefined)).data,
  });

  const all = alarmsQuery.data ?? [];
  const filtered = useMemo(
    () => (severityFilter ? all.filter((a) => a.severity === severityFilter) : all),
    [all, severityFilter],
  );

  const counts = useMemo(() => {
    const c = { critical: 0, fault: 0, warning: 0, last7: 0 };
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    for (const a of all) {
      c[a.severity]++;
      if (a.timestampUtc && new Date(a.timestampUtc).getTime() > cutoff) c.last7++;
    }
    return c;
  }, [all]);

  return (
    <>
      <PageHeader
        title="Alarms"
        subtitle="Machine fault and safety events across your subscribed machines."
        actions={
          <>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <span className="text-slate-500">Machine</span>
              <select
                value={machineFilter}
                onChange={(e) => setMachineFilter(e.target.value)}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
              >
                <option value="">All machines</option>
                {machinesQuery.data?.map((m) => (
                  <option key={m.uid} value={m.id ?? ''}>
                    {m.id}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <span className="text-slate-500">Severity</span>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
              >
                <option value="">All</option>
                <option value="critical">Critical</option>
                <option value="fault">Fault</option>
                <option value="warning">Warning</option>
              </select>
            </label>
          </>
        }
      />

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile label="Critical" value={String(counts.critical)} Icon={AlertCircle} />
        <KpiTile label="Faults" value={String(counts.fault)} Icon={AlertTriangle} />
        <KpiTile label="Warnings" value={String(counts.warning)} Icon={CheckCircle2} />
        <KpiTile label="Last 7 days" value={String(counts.last7)} />
      </section>

      <Card className="mt-6" title={`Events · ${filtered.length}`}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-4 font-medium">Severity</th>
                <th className="py-2 pr-4 font-medium">Machine</th>
                <th className="py-2 pr-4 font-medium">Alarm</th>
                <th className="py-2 pr-4 font-medium">Time</th>
                <th className="py-2 pr-4 font-medium">Notification</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {filtered.map((a) => {
                const style = SEVERITY_STYLE[a.severity];
                return (
                  <tr key={a.uid} className="hover:bg-slate-50">
                    <td className="py-2.5 pr-4">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${style.pill}`}
                      >
                        <span className={`size-1.5 rounded-full ${style.dot}`} />
                        {style.label}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 font-mono text-xs text-slate-600">{a.machineId}</td>
                    <td className="py-2.5 pr-4 font-medium text-slate-800">{a.label}</td>
                    <td className="py-2.5 pr-4 text-slate-500">{formatDate(a.timestampUtc)}</td>
                    <td className="py-2.5 pr-4">
                      {a.emailSent ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                          <Mail className="size-3.5" /> Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                          <MailX className="size-3.5" /> Pending
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-xs text-slate-400">
                    No alarms match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
};

export default AlarmsView;
